import React from 'react';
import classNames from 'classnames';
import { Icon } from 'ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import HighFiveShareableLink from 'components/shared/HighFiveShareableLink';
import SkippedSuccess from 'components/Practice/Recording/SkippedSuccess';
import SkippedFailure from 'components/Practice/Recording/SkippedFailure';
import { useFeatureFlags } from 'components/shared/hooks';
import AutomatedAnalysisAudioMessage from 'components/Practice/shared/AutomatedAnalysisAudioMessage';
import ActionPrompt from './ActionPrompt';
import IlosVideoEmbed from '../Ilos/IlosVideoEmbed';
import VideoEmbed from '../../VideoEmbed';

const ResponseOption = props => {
  const {
    givenAnswer,
    skipped,
    recording,
    finished,
    i18n,
    hasIlosFeature,
    token,
    errorMessage,
    unloadWebcam,
    loadRecording,
    saveVideo,
    isWebcamRecording,
    isScreenRecording,
    apiKey,
    skipRecording,
    givenAnswerIsIlos,
    previewing,
    questionId,
    canGatherDataOnTechnicalIssues,
    webcamPlaybackEnabled,
    recordWebcamEvent,
    removeAbilityToSkip,
    setUploadFinished,
    setUploadStarted,
    handleBadUploadType,
    disabled,
    technicalDifficultiesReported,
    showAnswerAlreadySubmitted,
    sendFeedbackError,
    recordingRecord,
  } = props;

  const seismicRecordingEnabled = useFeatureFlags(
    'readiness-enable-seismic-recording',
  );

  const seismicWebcamPracticeUploadEnabled = useFeatureFlags(
    'readiness-enable-seismic-upload-webcam-response',
  );

  const seismicBiometricCompliance = useFeatureFlags(
    'readiness-enable-biometric-compliance',
  );

  const submittedIlosVideo = finished && givenAnswerIsIlos;
  const givenAnswerIsNotIlos = givenAnswer && !givenAnswerIsIlos;
  const useIlosRecorder =
    isScreenRecording || (hasIlosFeature && isWebcamRecording);
  const recordButtonText = t('questions.question.webcam_recording.record');
  const uploadButtonText = t('questions.question.webcam_recording.upload');
  const mediaButtonClass = classNames({
    'QuestionElement--disabled': disabled,
  });

  if (skipped) {
    return sendFeedbackError ? (
      <SkippedFailure
        {...props}
        mediaButtonClass={mediaButtonClass}
        recordButtonText={recordButtonText}
        uploadButtonText={uploadButtonText}
        finished={finished}
        disabled={disabled}
        showTechnicalDifficulties
        reloadRecording={loadRecording}
      />
    ) : (
      <SkippedSuccess
        {...props}
        mediaButtonClass={mediaButtonClass}
        recordButtonText={recordButtonText}
        uploadButtonText={uploadButtonText}
        finished={finished}
        disabled={disabled}
        showTechnicalDifficulties
        reloadRecording={loadRecording}
      />
    );
  }

  if (
    givenAnswerIsNotIlos ||
    (!useIlosRecorder && !skipped && !submittedIlosVideo)
  ) {
    return (
      <VideoEmbed
        questionId={questionId}
        progressId={props.progressId}
        token={token}
        givenAnswer={givenAnswer}
        errorMessage={errorMessage}
        unloadWebcam={unloadWebcam}
        i18n={i18n}
        finished={finished}
        loadRecording={loadRecording}
        saveVideo={saveVideo}
        apiKey={apiKey}
        skipRecording={skipRecording}
        previewing={previewing}
        canGatherDataOnTechnicalIssues={canGatherDataOnTechnicalIssues}
        webcamPlaybackEnabled={webcamPlaybackEnabled}
        recordWebcamEvent={recordWebcamEvent}
        removeAbilityToSkip={removeAbilityToSkip}
        lessonTaking
        setUploadFinished={setUploadFinished}
        setUploadStarted={setUploadStarted}
        handleBadUploadType={handleBadUploadType}
        hasZiggeoForAllMedia={false}
        technicalDifficultiesReported={technicalDifficultiesReported}
        showAnswerAlreadySubmitted={showAnswerAlreadySubmitted}
        skipped={skipped}
        sendFeedbackError={sendFeedbackError}
        allowRecord={!seismicRecordingEnabled}
        recordingRecord={recordingRecord}
        seismicRecordingEnabled={seismicRecordingEnabled}
        seismicWebcamPracticeUploadEnabled={seismicWebcamPracticeUploadEnabled}
        seismicBiometricCompliance={seismicBiometricCompliance}
        hasAutomatedAnalysis={props.hasAutomatedAnalysis}
        enabledAutomatedAnalysis={props.enabledAutomatedAnalysis}
        setEnabledAutomatedAnalysis={props.setEnabledAutomatedAnalysis}
        completedAt={props.completedAt}
      />
    );
  }

  if (recording || (givenAnswer && !skipped)) {
    return <IlosVideoEmbed {...props} />;
  }
  return <ActionPrompt {...props} />;
};

const Response = props => (
  <div className="Response">
    <AutomatedAnalysisAudioMessage
      hasAutomatedAnalysis={props.hasAutomatedAnalysis}
    />
    {props.videoUploading && (
      <div className="bg-gray-lightest p-12 fz-14 lh-20 mb-32">
        <Icon name="warning_sign" className="mr-8" />
        <Text className="mr-4" variant={TEXT_VARIANTS.BODY_BOLD}>
          {t('questions.question.video_response.new_navigation_warning_bold')}
        </Text>
        <Text variant={TEXT_VARIANTS.BODY}>
          {t('questions.question.video_response.new_navigation_warning')}
        </Text>
      </div>
    )}
    <ResponseOption {...props} />
    {props.givenAnswer && !props.skipped && (
      <HighFiveShareableLink
        questionId={props.questionId}
        progressId={props.progressId}
        hasAnswerBeenSubmitted={props.hasAnswerBeenSubmitted}
        isGradable={props.isGradable}
      />
    )}
  </div>
);

export default Response;
